/**
 * Sample Data for Development and Testing
 */

import type { Provider, AppointmentSlot } from '@/types';
import { addDays, setHours, setMinutes, startOfDay } from 'date-fns';

// Service types available
export interface ServiceType {
  id: string;
  name: string;
  duration: number;
}

export const SAMPLE_SERVICES: ServiceType[] = [
  { id: 'evaluation', name: 'Evaluation (30 mins)', duration: 30 },
  { id: 'consultation', name: 'Consultation (45 mins)', duration: 45 },
  { id: 'follow-up', name: 'Follow-up (15 mins)', duration: 15 },
  { id: 'physical', name: 'Physical Exam (60 mins)', duration: 60 },
];

// Location types
export interface LocationType {
  id: string;
  name: string;
}

export const SAMPLE_LOCATIONS: LocationType[] = [
  { id: 'virtual', name: 'Virtual' },
  { id: 'main-office', name: 'Main Office' },
  { id: 'downtown', name: 'Downtown Clinic' },
  { id: 'north', name: 'North Branch' },
];

export const SAMPLE_PROVIDERS: Provider[] = [
  {
    id: 'dr-sophie-clark',
    name: 'Dr. Sophie Clark',
    title: 'MD',
    specialty: 'Family medicine',
    bio: 'Board certified family medicine clinician with over 10 years of experience in comprehensive primary care.',
    // avatarUrl: '/providers/dr-sophie-clark.jpg',
    acceptingNewPatients: true,
  },
  {
    id: 'dr-jason-lee',
    name: 'Dr. Jason Lee',
    title: 'MD',
    specialty: 'Pediatrician',
    bio: 'Dedicated pediatrician with 10 years of experience in child health and development.',
    // avatarUrl: '/providers/dr-jason-lee.jpg',
    acceptingNewPatients: true,
  },
  {
    id: 'dr-emily-tran',
    name: 'Dr. Emily Tran',
    title: 'MD',
    specialty: 'Cardiologist',
    bio: 'Cardiologist with 12 years of experience in diagnosing and treating heart conditions.',
    // avatarUrl: '/providers/dr-emily-tran.jpg',
    acceptingNewPatients: true,
  },
];

/**
 * Generate sample appointment slots for the next 14 days
 */
export function generateSampleSlots(providerId: string): AppointmentSlot[] {
  const slots: AppointmentSlot[] = [];
  const today = startOfDay(new Date());

  // Generate slots for the next 14 days
  for (let dayOffset = 0; dayOffset < 14; dayOffset++) {
    const date = addDays(today, dayOffset);

    // Skip weekends
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      continue;
    }

    // Morning slots: 9:00 AM - 11:30 AM
    for (let hour = 9; hour < 12; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        if (hour === 11 && minute === 30) break; // Stop at 11:30

        const startTime = setMinutes(setHours(date, hour), minute);
        const endTime = new Date(startTime.getTime() + 30 * 60000); // 30 minutes

        slots.push({
          id: `${providerId}-${startTime.getTime()}`,
          providerId,
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          duration: 30,
          appointmentType: 'Office Visit',
          available: true,
        });
      }
    }

    // Afternoon slots: 12:00 PM - 2:30 PM
    for (let hour = 12; hour < 15; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        if (hour === 14 && minute === 30) break; // Stop at 2:30

        const startTime = setMinutes(setHours(date, hour), minute);
        const endTime = new Date(startTime.getTime() + 30 * 60000); // 30 minutes

        slots.push({
          id: `${providerId}-${startTime.getTime()}`,
          providerId,
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          duration: 30,
          appointmentType: 'Evaluation',
          available: true,
        });
      }
    }

    // Evening slots: 3:00 PM - 5:00 PM
    for (let hour = 15; hour < 17; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const startTime = setMinutes(setHours(date, hour), minute);
        const endTime = new Date(startTime.getTime() + 30 * 60000); // 30 minutes

        slots.push({
          id: `${providerId}-${startTime.getTime()}`,
          providerId,
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          duration: 30,
          appointmentType: 'Evaluation',
          available: true,
        });
      }
    }
  }

  return slots;
}

/**
 * Get all sample slots for all providers
 */
export function getAllSampleSlots(): AppointmentSlot[] {
  return SAMPLE_PROVIDERS.flatMap((provider) => generateSampleSlots(provider.id));
}
